import React, { useState } from "react";
import { GridGenerator } from "@actinc/dls";
import { Delete as DeleteIcon, Plus as PlusIcon } from "@actinc/dls/icons";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";

import {
  ControlledFormEditor,
  ControlledFormSelect,
  DraggableDialog,
} from "../../../../components";
import { getLetter } from "../../../../services/utils";

const CreateArtTaskDialog = ({ assets, object, objectType, onClose, open }) => {
  const defaultValues = {
    component: null,
    description: "",
    file: null,
    format: null,
    type: null,
  };
  const { control, getValues, resetField, reset } = useFormContext();
  const {
    fields: components,
    append,
    remove,
  } = useFieldArray({
    control,
    name: "imageDetails",
  });
  const [componentNames, setComponentNames] = useState([]);

  const handleDialogOpen = () => {
    const currentValue = getValues("imageDetails");
    if (!currentValue || currentValue.length === 0) {
      reset({
        ...getValues(),
        imageDetails: [{ ...defaultValues }],
      });
    }
  };

  const handleComponentChange = (index) => (e) => {
    setComponentNames(
      components.map((_component, i) =>
        i === index ? e.target.value : componentNames[i] || "",
      ),
    );
  };

  const handleCancel = () => {
    resetField("imageDetails", {
      defaultValue: [{ ...defaultValues }],
    });
    onClose();
  };

  const onSubmit = () => {
    const value = getValues("imageDetails");
    onClose(value);
  };

  return (
    <DraggableDialog
      maxWidth="md"
      onClose={handleCancel}
      open={open}
      TransitionProps={{ onEnter: handleDialogOpen }}
      title={
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flex: 1,
            justifyContent: "space-between",
          }}
        >
          <span>Create Art Task</span>
          <Button
            color="inherit"
            onClick={() => append({ ...defaultValues })}
            size="small"
            startIcon={<PlusIcon />}
            variant="outlined"
          >
            Add Image Request
          </Button>
        </Box>
      }
    >
      <DialogContent>
        {components.map((component, index) => {
          const name = componentNames[index];
          return (
            <Box key={component.id} sx={{ marginBottom: 2 }}>
              {!!components.length && (
                <Divider>
                  <Typography variant="subtitle">
                    Image Request {index + 1}
                    {name && `: ${name}`}
                  </Typography>
                  <IconButton
                    aria-label={`delete component ${index + 1}`}
                    disabled={components.length === 1}
                    onClick={() => remove(index)}
                    size="small"
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Divider>
              )}
              <GridGenerator
                groups={[
                  [
                    {
                      children:
                        objectType === "item" ? (
                          <ControlledFormSelect
                            control={control}
                            fullWidth={true}
                            id={`imageDetails[${index}].component`}
                            label="Component"
                            name={`imageDetails[${index}].component`}
                            onChange={handleComponentChange(index)}
                            options={object.interactions
                              .reduce(
                                (acc, interaction) => {
                                  if (
                                    interaction.__typename !==
                                    "ChoiceInteraction"
                                  )
                                    return [...acc];
                                  return [
                                    ...acc,
                                    ...interaction.foils.map(
                                      (_foil, index) =>
                                        `${
                                          interaction.identifier
                                        }: Foil ${getLetter(index)}`,
                                    ),
                                  ];
                                },
                                ["Stem"],
                              )
                              .map((s) => ({ label: s, value: s }))}
                          />
                        ) : null,
                      key: `imageDetails[${index}].component`,
                    },
                    {
                      children:
                        objectType === "item" ? (
                          <ControlledFormSelect
                            control={control}
                            fullWidth={true}
                            id={`imageDetails[${index}].type`}
                            label="Art Type"
                            name={`imageDetails[${index}].type`}
                            options={["Standard", "Hotspot", "Dragger"].map(
                              (s) => ({ label: s, value: s }),
                            )}
                          />
                        ) : null,
                      key: `imageDetails[${index}].type`,
                    },
                  ],
                  [
                    {
                      children: (
                        <ControlledFormSelect
                          control={control}
                          fullWidth={true}
                          id={`imageDetails[${index}].file`}
                          label="Reference Image Asset"
                          name={`imageDetails[${index}].file`}
                          options={assets.map(({ name }) => ({
                            label: name,
                            value: `assets/${object.id}/${name}`,
                          }))}
                        />
                      ),
                      key: `imageDetails[${index}].file`,
                    },
                    {
                      children: (
                        <ControlledFormSelect
                          control={control}
                          fullWidth={true}
                          id={`imageDetails[${index}].format`}
                          label="Color Format"
                          name={`imageDetails[${index}].format`}
                          options={["Black and White", "Full Color"].map(
                            (s) => ({ label: s, value: s }),
                          )}
                        />
                      ),
                      key: `imageDetails[${index}].format`,
                    },
                  ],
                  [
                    {
                      children: (
                        <ControlledFormEditor
                          control={control}
                          fullWidth={true}
                          id={`imageDetails[${index}].description`}
                          label="Description"
                          name={`imageDetails[${index}].description`}
                        />
                      ),
                      key: `imageDetails[${index}].description`,
                    },
                  ],
                ]}
              />
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCancel} variant="text">
          Cancel
        </Button>
        <Button
          color="primary"
          form="createArtTaskForm"
          onClick={() => onSubmit()}
          variant="contained"
        >
          Submit
        </Button>
      </DialogActions>
    </DraggableDialog>
  );
};

export { CreateArtTaskDialog };
